import { useRef, useState } from 'react';
import { Alert, Button, Modal, Slider, Space, Typography } from 'antd';
import {
  getTeacherAvatarStyle,
  teacherAvatarCropSchema,
  type TeacherAvatarCrop,
} from '@luobo/contracts';
import { moveCropPosition } from './image-crop';

export function TeacherAvatarEditor({
  image,
  imageOrigin,
  value,
  onChange,
}: {
  image: string;
  imageOrigin?: string;
  value: unknown;
  onChange: (crop: TeacherAvatarCrop | undefined) => void;
}) {
  const parsed = teacherAvatarCropSchema.safeParse(value);
  const saved = parsed.success && parsed.data.sourceImage === image ? parsed.data : undefined;
  const src = imageOrigin && image.startsWith('/') ? new URL(image, imageOrigin).href : image;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TeacherAvatarCrop | undefined>(saved);
  const [error, setError] = useState('');
  const drag = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    crop: TeacherAvatarCrop;
    overflowX: number;
    overflowY: number;
  } | null>(null);

  return (
    <div className="teacher-avatar-editor">
      {image ? (
        <span className="teacher-avatar-preview">
          <img src={src} alt="团队成员小头像预览" style={getTeacherAvatarStyle(image, saved)} />
        </span>
      ) : (
        <Typography.Text type="secondary">请先上传老师照片。</Typography.Text>
      )}
      <Space wrap>
        <Button
          disabled={!image}
          onClick={() => {
            setDraft(saved);
            setError('');
            setOpen(true);
          }}
        >
          {saved ? '调整小头像取景' : '设置小头像取景'}
        </Button>
        {saved && <Button onClick={() => onChange(undefined)}>恢复默认取景</Button>}
      </Space>
      <Typography.Text type="secondary">
        小头像使用同一张老师照片，独立调整头肩取景；这里的调整只影响鼠标悬浮时的圆形小头像。
      </Typography.Text>
      <Modal
        title="调整团队成员小头像"
        open={open}
        okText="应用头像取景"
        cancelText="取消"
        okButtonProps={{ disabled: !draft || !!error }}
        onCancel={() => setOpen(false)}
        onOk={() => {
          if (!draft || error) return;
          onChange(draft);
          setOpen(false);
        }}
        afterClose={() => {
          drag.current = null;
        }}
      >
        <div className="image-crop-editor">
          <Typography.Text type="secondary">
            拖动照片并调整缩放，让头部和肩部位于圆形区域内。
          </Typography.Text>
          <div
            className="image-crop-frame teacher-avatar-crop-frame"
            onPointerDown={(event) => {
              if (!draft || error || !event.isPrimary || event.button !== 0) return;
              const frame = event.currentTarget.getBoundingClientRect();
              const side = Math.min(draft.imageWidth, draft.imageHeight) / draft.zoom;
              event.currentTarget.setPointerCapture(event.pointerId);
              drag.current = {
                pointerId: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
                crop: draft,
                overflowX: frame.width * (draft.imageWidth / side - 1),
                overflowY: frame.height * (draft.imageHeight / side - 1),
              };
              event.preventDefault();
            }}
            onPointerMove={(event) => {
              const start = drag.current;
              if (!start || start.pointerId !== event.pointerId) return;
              setDraft({
                ...start.crop,
                x: moveCropPosition(start.crop.x, event.clientX - start.clientX, start.overflowX),
                y: moveCropPosition(start.crop.y, event.clientY - start.clientY, start.overflowY),
              });
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId))
                event.currentTarget.releasePointerCapture(event.pointerId);
              drag.current = null;
            }}
            onPointerCancel={() => {
              drag.current = null;
            }}
            onLostPointerCapture={() => {
              drag.current = null;
            }}
          >
            {open && (
              <img
                src={src}
                alt="小头像取景预览"
                draggable={false}
                onLoad={(event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  setError('');
                  setDraft((current) => ({
                    sourceImage: image,
                    imageWidth: naturalWidth,
                    imageHeight: naturalHeight,
                    zoom: current?.zoom ?? 1,
                    x: current?.x ?? 0,
                    y: current?.y ?? 0,
                  }));
                }}
                onError={() => setError('照片加载失败，请检查图片或重新上传。')}
                style={draft ? getTeacherAvatarStyle(image, draft) : { visibility: 'hidden' }}
              />
            )}
          </div>
          {error && <Alert type="error" showIcon title={error} />}
          <label>
            <span>缩放</span>
            <Slider
              ariaLabelForHandle="小头像缩放"
              min={1}
              max={4}
              step={0.05}
              value={draft?.zoom ?? 1}
              disabled={!draft}
              onChange={(zoom) => setDraft((current) => current && { ...current, zoom })}
            />
          </label>
          <label>
            <span>水平位置</span>
            <Slider
              ariaLabelForHandle="小头像水平位置"
              min={-100}
              max={100}
              value={draft?.x ?? 0}
              disabled={!draft}
              onChange={(x) => setDraft((current) => current && { ...current, x })}
            />
          </label>
          <label>
            <span>垂直位置</span>
            <Slider
              ariaLabelForHandle="小头像垂直位置"
              min={-100}
              max={100}
              value={draft?.y ?? 0}
              disabled={!draft}
              onChange={(y) => setDraft((current) => current && { ...current, y })}
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}
